import { Component, Host, h, Listen, Prop, State } from "@stencil/core";
import api from "../../utils/api";
import { OnboardingGuideInterface } from "./onboarding-guide-interface";
import { RenderContentStep } from "./steps/content";
import { RenderTableOfContentsStep } from "./steps/table-of-contents";

@Component({
    tag: "ux-iws-onboarding-guide",
    styleUrl: "onboarding-guide.scss",
})
export class OnboardingGuide {
    private baseClass = "ux-iws-onboarding-guide";

    /** API endpoint url to request onboarding steps data */
    @Prop() readonly dataEndpoint: string = null;

    /** Selector used to find the data script tag in the DOM tree */
    @Prop() readonly dataScriptId: string;

    /** Sets the opened state of the modal  */
    @Prop() readonly opened: boolean = false;

    @State() responseData: OnboardingGuideInterface;
    @State() currentStepIndex: number = null;
    @State() lastStepIndex: number = null;

    componentDidLoad() {
        this.loadData();
    }

    @Listen("uxOnboardingGuideChange")
    onboardingTourChange(event: CustomEvent) {
        this.currentStepIndex = event.detail.stepIndex;
    }

    private loadData = async () => {
        if (typeof this.dataEndpoint === "string") {
            try {
                if (this.dataEndpoint.includes("mock-data")) {
                    await new Promise((resolve) => setTimeout(resolve, 1000));
                }
                this.responseData = await api<OnboardingGuideInterface>(
                    this.dataEndpoint
                );
                this.setInitialStepIndex(this.responseData);
            } catch (e) {
                console.log("Error: ", e);
            }
        }

        const scriptElement = document.querySelector<HTMLScriptElement>(
            "#" + this.dataScriptId
        );
        if (scriptElement !== null) {
            this.responseData = JSON.parse(scriptElement.innerText);
            this.setInitialStepIndex(this.responseData);
        }
    };

    private setInitialStepIndex = (responseData) => {
        if (responseData.steps) {
            this.currentStepIndex = 0;
            this.lastStepIndex = responseData.steps.length - 1;
        } else {
            console.log("Error: ", responseData);
        }
    };

    private rememberLogin = async () => {
        try {
            const buttonRememberLogin = this.responseData.buttonRememberLogin;

            if (!buttonRememberLogin || !buttonRememberLogin.url) {
                return;
            }

            const url = buttonRememberLogin.url;

            if (url.includes("mock-data")) {
                await new Promise((resolve) => setTimeout(resolve, 1000));
            }
            const response = await api<Record<string, any>>(url);
            if (!response.success) {
                console.log("Error: ", response);
            }
        } catch (e) {
            console.log("Error: ", e);
        }
    };

    private neverShowAgain = async () => {
        try {
            const buttonNeverShowAgain = this.responseData.buttonNeverShowAgain;

            if (!buttonNeverShowAgain || !buttonNeverShowAgain.url) {
                return;
            }

            const url = buttonNeverShowAgain.url;

            if (url.includes("mock-data")) {
                await new Promise((resolve) => setTimeout(resolve, 1000));
            }
            const response = await api<Record<string, any>>(url);
            if (!response.success) {
                console.log("Error: ", response);
            }
        } catch (e) {
            console.log("Error: ", e);
        }
    };

    private goToTableOfContents() {
        if (this.responseData?.steps) {
            const tocIndex = this.responseData.steps.findIndex(
                (step) => step.type === "table-of-contents"
            );
            if (tocIndex !== -1) {
                this.currentStepIndex = tocIndex;
            } else {
                console.warn("Table of contents step not found");
            }
        }
    }

    private handleTableOfContentsClick(index: number) {
        this.currentStepIndex = index;
    }

    private renderSteps(step) {
        switch (step.type) {
            case "intro":
                return RenderContentStep(step, this.baseClass);
            case "table-of-contents":
                return RenderTableOfContentsStep(
                    step,
                    this.responseData,
                    this.baseClass,
                    this.handleTableOfContentsClick.bind(this)
                );
            case "content":
                return RenderContentStep(step, this.baseClass);
            default:
                console.warn(`Unknown step type: ${step.type}`);
                return null;
        }
    }

    render() {
        return (
            <Host class={this.baseClass}>
                <ux-onboarding-guide
                    opened={this.opened}
                    current={this.currentStepIndex}
                    auto-height="1024"
                >
                    <div slot="progress-bar-button">
                        <ux-button
                            variant={
                                this.responseData?.buttonProgressBar?.variant
                            }
                            icon="home"
                            onclick={() => this.goToTableOfContents()}
                        >
                            {this.responseData?.buttonProgressBar?.label}
                        </ux-button>
                    </div>
                    {!this.responseData || !this.responseData.steps ? (
                        <div slot="spinner">
                            <ux-loader variant="spinner"></ux-loader>
                        </div>
                    ) : (
                        [
                            this.responseData?.steps.map((step, index) => (
                                <div key={index}>{this.renderSteps(step)}</div>
                            )),
                            <div
                                class={`${this.baseClass}__custom-navigation`}
                                slot="navigation-0"
                            >
                                <div
                                    class={`${this.baseClass}__button-wrapper`}
                                >
                                    {this.responseData?.buttonRememberLogin && (
                                        <ux-button
                                            onClick={this.rememberLogin}
                                            data-trigger-modal="close,ux-onboarding-guide-modal"
                                        >
                                            {
                                                this.responseData
                                                    ?.buttonRememberLogin?.label
                                            }
                                        </ux-button>
                                    )}
                                    {this.responseData?.buttonCloseGuide && (
                                        <ux-button data-trigger-modal="close,ux-onboarding-guide-modal">
                                            {
                                                this.responseData
                                                    ?.buttonCloseGuide?.label
                                            }
                                        </ux-button>
                                    )}
                                </div>
                                <div
                                    class={`${this.baseClass}__button-wrapper`}
                                >
                                    {this.responseData?.buttonStartGuide && (
                                        <ux-button
                                            variant={
                                                this.responseData
                                                    ?.buttonStartGuide?.variant
                                            }
                                            onClick={() =>
                                                (this.currentStepIndex = 1)
                                            }
                                            class="ux-u-mb-zero"
                                        >
                                            {
                                                this.responseData
                                                    ?.buttonStartGuide?.label
                                            }
                                        </ux-button>
                                    )}
                                </div>
                            </div>,
                            <div
                                class={`${this.baseClass}__custom-navigation`}
                                slot={`navigation-${this.lastStepIndex}`}
                            >
                                <div
                                    class={`${this.baseClass}__button-wrapper`}
                                >
                                    {this.responseData?.buttonPrev && (
                                        <ux-button
                                            onClick={() =>
                                                this.currentStepIndex--
                                            }
                                        >
                                            {
                                                this.responseData?.buttonPrev
                                                    ?.label
                                            }
                                        </ux-button>
                                    )}
                                    {this.responseData
                                        ?.buttonNeverShowAgain && (
                                        <ux-button
                                            onClick={this.neverShowAgain}
                                            data-trigger-modal="close,ux-onboarding-guide-modal"
                                        >
                                            {
                                                this.responseData
                                                    ?.buttonNeverShowAgain
                                                    ?.label
                                            }
                                        </ux-button>
                                    )}
                                </div>
                                <div
                                    class={`${this.baseClass}__button-wrapper`}
                                >
                                    {this.responseData?.buttonFinishGuide && (
                                        <ux-button data-trigger-modal="close,ux-onboarding-guide-modal">
                                            {
                                                this.responseData
                                                    ?.buttonFinishGuide?.label
                                            }
                                        </ux-button>
                                    )}
                                </div>
                            </div>,
                            <div slot="button-left">
                                {this.responseData?.buttonPrev?.label}
                            </div>,
                            <div slot="button-right">
                                {this.responseData?.buttonNext?.label}
                            </div>,
                        ]
                    )}
                </ux-onboarding-guide>
            </Host>
        );
    }
}
